<script>
  import { createEventDispatcher } from "svelte";
  import backend from "../../../../../server";
  import Dropdown from "../../../ui/Dropdown.svelte";

  export let clientId = "";
  export let currency = "";
  export let value = "";
  export let multiple = false;
  export let disabled = false;
  export let lockedValues = [];
  export let pageSize = 25;
  export let clientParam = "client";
  export let valueKey = "id";
  export let labelKey = "username_origin";
  export let placeholder = "Seleccionar jugador...";
  export let listPlayers = (params) => backend.users.list(params);
  export let staticPlayers = null;
  export let strictCurrency = false;
  export let initialOptions = [];
  export let players = [];

  const dispatch = createEventDispatcher();

  let options = [];
  let playersByValue = new Map();
  let currentOptionValues = [];
  let page = 1;
  let search = "";
  let loading = false;
  let hasMore = false;
  let requestSequence = 0;
  let clientSnapshot;

  function optionValue(player) {
    return String(player?.[valueKey] ?? player?.id ?? "");
  }

  function selectedValues() {
    return multiple
      ? (Array.isArray(value) ? value : []).map(String)
      : value === null || value === undefined || value === ""
        ? []
        : [String(value)];
  }

  function rebuildOptions() {
    const visibleValues = [...currentOptionValues];
    for (const selectedValue of selectedValues()) {
      if (!visibleValues.includes(selectedValue) && playersByValue.has(selectedValue)) {
        visibleValues.push(selectedValue);
      }
    }
    options = visibleValues.map((id) => playersByValue.get(id)).filter(Boolean);
  }

  function canonicalValue(selectedValue) {
    const player = playersByValue.get(String(selectedValue));
    return player?.[valueKey] ?? selectedValue;
  }

  function alignSelectedValueTypes() {
    if (multiple) {
      value = (Array.isArray(value) ? value : []).map(canonicalValue);
      lockedValues = (Array.isArray(lockedValues) ? lockedValues : []).map(
        canonicalValue,
      );
    } else if (value !== null && value !== undefined && value !== "") {
      value = canonicalValue(value);
    }
  }

  async function load({ targetPage = 1, query = search, append = false } = {}) {
    if (!clientId) {
      options = [];
      hasMore = false;
      return;
    }

    const sequence = ++requestSequence;
    loading = true;
    try {
      const params = {
        page: targetPage,
        xpage: pageSize,
        search: query.trim(),
        [clientParam]: clientId,
      };
      if (currency) params.currency = currency;

      const response = Array.isArray(staticPlayers)
        ? {
            data: {
              list: staticPlayers
                .filter((player) => {
                  const needle = query.trim().toLowerCase();
                  if (!needle) return true;
                  return [player?.id, player?.username, player?.username_origin]
                    .some((field) => String(field ?? "").toLowerCase().includes(needle));
                })
                .slice((targetPage - 1) * pageSize, targetPage * pageSize),
            },
          }
        : await listPlayers(params);
      if (sequence !== requestSequence) return;

      const payload = response?.data ?? response;
      const rawReceived = Array.isArray(payload?.list)
        ? payload.list
        : Array.isArray(payload)
          ? payload
          : [];
      const normalizedCurrency = String(currency ?? "").trim().toUpperCase();
      const received = strictCurrency && normalizedCurrency
        ? rawReceived.filter(
            (player) => String(player?.currency ?? "").trim().toUpperCase() === normalizedCurrency,
          )
        : rawReceived;
      const receivedValues = [];

      for (const player of received) {
        const normalizedValue = optionValue(player);
        if (!normalizedValue) continue;
        playersByValue.set(normalizedValue, player);
        receivedValues.push(normalizedValue);
      }
      alignSelectedValueTypes();
      players = [...playersByValue.values()];

      currentOptionValues = append
        ? [...new Set([...currentOptionValues, ...receivedValues])]
        : receivedValues;
      page = targetPage;
      search = query.trim();
      hasMore = rawReceived.length >= pageSize;
      rebuildOptions();
    } catch (error) {
      if (sequence !== requestSequence) return;
      console.error("Error cargando jugadores:", error);
      if (!append) {
        currentOptionValues = [];
        rebuildOptions();
      }
      hasMore = false;
      dispatch("error", error);
    } finally {
      if (sequence === requestSequence) loading = false;
    }
  }

  async function handleSearch(event) {
    search = event.detail ?? "";
    page = 1;
    hasMore = false;
    await load({ targetPage: 1, query: search, append: false });
  }

  async function handleLoadMore() {
    if (loading || !hasMore) return;
    await load({ targetPage: page + 1, query: search, append: true });
  }

  function handleChange(event) {
    value = event.detail;
    rebuildOptions();
    dispatch("change", value);
  }

  $: {
    const nextClientSnapshot = [
      String(clientId ?? ""),
      String(currency ?? "").trim().toUpperCase(),
      clientParam,
    ].join("|");
    if (nextClientSnapshot !== clientSnapshot) {
      const isFirstClientLoad = clientSnapshot === undefined;
      clientSnapshot = nextClientSnapshot;
      requestSequence++;
      playersByValue = new Map();
      for (const player of Array.isArray(initialOptions) ? initialOptions : []) {
        const normalizedValue = optionValue(player);
        if (normalizedValue) playersByValue.set(normalizedValue, player);
      }
      players = [...playersByValue.values()];
      currentOptionValues = [...playersByValue.keys()];
      options = [...players];
      page = 1;
      search = "";
      hasMore = false;
      value = multiple
        ? [...(Array.isArray(lockedValues) ? lockedValues : [])]
        : isFirstClientLoad
          ? value
          : "";
      if (clientId) load();
    }
  }
</script>

<Dropdown
  {options}
  {multiple}
  bind:value
  {lockedValues}
  {valueKey}
  {labelKey}
  isStructuredDescription={true}
  paramStructuredName={["username_origin", "currency"]}
  remote={true}
  {loading}
  {hasMore}
  {placeholder}
  disabled={disabled || !clientId}
  on:search={handleSearch}
  on:loadMore={handleLoadMore}
  on:change={handleChange}
/>
